import clsx from "clsx";
import type { FC } from "react";
import React, { memo, useMemo } from "react";
import { getUniqueGradeBodyTypeId } from "../../../../../../common-deprecated/features/apheleia/utils/aphUtils";
import { getCarDBAssetUrl } from "../../../../../shared-logic/utils/imageUtils";
import Button from "../../../../shared-components/css-modules/lexus/atoms/Button";
import Icon from "../../../../shared-components/css-modules/lexus/atoms/Icon";
import Swatch from "../../../../shared-components/css-modules/lexus/atoms/Swatch";
import Text from "../../../../shared-components/css-modules/lexus/atoms/Text";
import { useGradeSelectorSelector } from "../../../redux/hooks";
import { setColour } from "../../../redux/slices/GradeSelectorModelSlice";
import type { GradeSelectorDispatchType } from "../../../redux/store";
import { useGradeImage } from "../../../utils/gradeHooks";
import { getColourCodeById, getColoursOfGradeBodyType, getMotorizationData, getName } from "../../../utils/gradeUtils";
import { GradeSelectorMemoLabel } from "../../../utils/labels";
import type { GradeBodyTypeType } from "../../../utils/types/Model";
import EcoItems from "../selector/item/EcoItems";
import HighlightsView from "../selector/item/HighlightsView";
import commonStyles from "../selector/item/Item.module.scss";
import Price from "../selector/item/price/Price";
import Specs from "../selector/item/specs/EngineInfo";
import styles from "./Item.module.scss";
import { useDispatch } from "../../../../../../shared-logic/redux/store";
import { useFilterCarExtras } from "../../../utils/filterHooks";

const Item: FC<{
    gradeBodyType: GradeBodyTypeType;
    isSelected: boolean;
    onSelect?: (
        carId: string,
        name: string,
        selectedColourId: string,
        selectedColourCode: string,
        carExtras: string[],
    ) => void;
    onKeep?: () => void;
}> = ({ gradeBodyType, isSelected, onSelect, onKeep }) => {
    const dispatch = useDispatch<GradeSelectorDispatchType>();

    const modelId = useGradeSelectorSelector((state) => state.gradeSelectorModel.model.id);
    const compressedCcisPath = useGradeSelectorSelector((state) => state.commonSettings.compressedCcisPath);
    const { hasMultipleBodyTypes, bodyType, grade, engines } = gradeBodyType;
    const selectedColourId = gradeBodyType.selectedColourId ?? getColoursOfGradeBodyType(gradeBodyType)[0].id;
    const { wheeldrive } = getMotorizationData(gradeBodyType);
    const { car } = wheeldrive;
    const carExtras = useFilterCarExtras(car);

    const name = getName(gradeBodyType);
    const subModelName = gradeBodyType.submodel?.name;
    const colourCode = useMemo(
        () => getColourCodeById({ ...gradeBodyType, selectedColourId }),
        [gradeBodyType, selectedColourId],
    );

    const imageBase = useGradeImage(grade, bodyType, car, 331, 131, 27, "png", colourCode).src;
    const imageBase2x = useGradeImage(grade, bodyType, car, 331 * 2, 131 * 2, 27, "png", colourCode).src;

    const uniqueBodyTypeId = getUniqueGradeBodyTypeId(grade.id, gradeBodyType.bodyType.id, gradeBodyType.submodel?.id);

    const handleSelect = (): void => {
        onSelect?.(wheeldrive.car.CarID, name, selectedColourId, colourCode, carExtras);
    };

    const onColourSelect = (colourId: string): void => {
        dispatch(
            setColour({
                uniqueBodyTypeId,
                colourId,
            }),
        );
    };

    return (
        <article className={clsx(styles.wrapper, isSelected && styles.selected)}>
            <div>
                <header className={commonStyles.header}>
                    <div>
                        <div className={commonStyles["pre-title"]}>
                            <EcoItems modelId={modelId} engines={engines} limit={2} />
                        </div>
                        <div className={commonStyles.title}>
                            <Text tag="h2" variant="apheleia-h4">
                                {name}
                            </Text>
                            {subModelName && (
                                <Text tag="h4" variant="apheleia-h6">
                                    {subModelName}
                                </Text>
                            )}
                        </div>

                        {hasMultipleBodyTypes && (
                            <Text tag="h3" variant="apheleia-body-large">
                                {bodyType.name}
                            </Text>
                        )}
                    </div>
                </header>
                <div>
                    <button
                        type="button"
                        className={commonStyles.image}
                        onClick={() => (isSelected ? onKeep?.() : handleSelect())}
                    >
                        <img src={imageBase} srcSet={`${imageBase} 1x, ${imageBase2x} 2x`} alt={name} />
                    </button>
                </div>
                {/* Colors */}
                <div className={styles.colours}>
                    {wheeldrive.colors.map((colour, index) => (
                        <Swatch
                            key={colour.id}
                            className={styles.swatch}
                            image={getCarDBAssetUrl(colour.asset, compressedCcisPath, 48, 48)}
                            fill={colour.rgb}
                            active={(!selectedColourId && index === 0) || colour.id === selectedColourId}
                            onClick={() => {
                                onColourSelect(colour.id);
                            }}
                        />
                    ))}
                </div>
                <div>
                    <div className={commonStyles.price}>
                        <Price wheeldrive={wheeldrive} selectedColourId={gradeBodyType.selectedColourId} />
                    </div>
                    <div className={styles.features}>
                        <HighlightsView gradeBodyType={gradeBodyType} />
                    </div>
                </div>
            </div>
            <Specs gradeBodyType={gradeBodyType} />
            <footer className={styles.footer}>
                {isSelected && (
                    <Button
                        onClick={() => {
                            onKeep?.();
                        }}
                        variant="btn-secondary"
                        className={clsx(commonStyles.button, commonStyles.selected)}
                    >
                        <GradeSelectorMemoLabel label="gradeSelectorKeepGrade" />
                        <Icon variant="arrow-right" sizing="sm" aria-hidden="true" />
                    </Button>
                )}
                {!isSelected && (
                    <Button onClick={handleSelect} variant="btn-secondary" className={commonStyles.button}>
                        <GradeSelectorMemoLabel label="gradeSelectorSelectGrade" />
                        <Icon variant="arrow-right" sizing="sm" aria-hidden="true" />
                    </Button>
                )}
            </footer>
        </article>
    );
};

export default memo(Item);
